import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  alpha,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  RocketLaunch,
  Business,
  Analytics,
  Assessment,
  CloudSync,
  Dashboard,
  TrendingUp,
  Security,
  AutoAwesome,
  ArrowBack,
  Phone,
  Email,
  LocationOn,
  Search,
  Inventory,
  AccountBalance,
  Settings,
  Build,
  Insights,
  Speed,
  VerifiedUser,
} from '@mui/icons-material';

const ServicesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const services = [
    {
      icon: <Assessment sx={{ fontSize: 50 }} />,
      title: 'Business Consultancy',
      description: 'We help you identify where you lose money and how you can improve floor efficiency. Our experts analyze your operations, cash flow patterns, and business processes to uncover hidden inefficiencies and revenue leaks.',
      features: [
        'Cash Flow Analysis & Optimization',
        'Revenue Leak Identification',
        'Floor Efficiency Improvements',
        'Process Optimization',
        'Cost Reduction Strategies',
        'Operational Audit',
      ],
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      icon: <CloudSync sx={{ fontSize: 50 }} />,
      title: 'SaaS Solutions Consultation',
      description: 'Find the perfect SaaS solutions tailored to your domain and improve your operations. We help you choose, implement, and optimize technology tools that drive efficiency and growth.',
      features: [
        'Domain-Specific SaaS Recommendations',
        'SaaS Implementation Support',
        'Operations Automation',
        'Technology Stack Optimization',
        'Integration & Migration Services',
        'Training & Onboarding',
      ],
      color: '#4CAF50',
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #2196F3 100%)',
    },
    {
      icon: <Analytics sx={{ fontSize: 50 }} />,
      title: 'Financial Analytics & Reporting',
      description: 'Get comprehensive financial insights and real-time reporting to make data-driven decisions. Transform your financial data into actionable intelligence for better business outcomes.',
      features: [
        'Real-Time Financial Dashboards',
        'Custom Financial Reports',
        'Predictive Analytics',
        'Performance Benchmarking',
        'KPI Tracking & Monitoring',
        'Data Visualization',
      ],
      color: '#FF9800',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #F44336 100%)',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 50 }} />,
      title: 'Growth Strategy & Planning',
      description: 'Develop strategic growth plans tailored to your SME. We help you create roadmaps, set goals, and execute strategies that drive sustainable business expansion.',
      features: [
        'Business Growth Planning',
        'Market Expansion Strategies',
        'Revenue Growth Optimization',
        'Scalability Planning',
        'Strategic Roadmapping',
        'Goal Setting & Tracking',
      ],
      color: '#9C27B0',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #E91E63 100%)',
    },
    {
      icon: <Security sx={{ fontSize: 50 }} />,
      title: 'Compliance & Risk Management',
      description: 'Stay compliant with regulations and manage business risks effectively. Ensure your SME meets all legal requirements while protecting your business from potential threats.',
      features: [
        'Regulatory Compliance Audits',
        'Risk Assessment & Mitigation',
        'Tax Compliance Support',
        'Documentation Management',
        'Security Best Practices',
        'Legal Advisory Services',
      ],
      color: '#00BCD4',
      gradient: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
    },
    {
      icon: <Speed sx={{ fontSize: 50 }} />,
      title: 'Process Automation & Efficiency',
      description: 'Automate repetitive tasks and streamline your business processes. Reduce manual work, minimize errors, and free up time to focus on growth and innovation.',
      features: [
        'Workflow Automation',
        'Task Automation Setup',
        'System Integration',
        'Efficiency Audits',
        'Process Redesign',
        'Productivity Optimization',
      ],
      color: '#E91E63',
      gradient: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 15s ease infinite',
        position: 'relative',
        overflow: 'hidden',
        scrollBehavior: 'smooth',
        '@keyframes gradientShift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
        '@keyframes fadeInUp': {
          from: {
            opacity: 0,
            transform: 'translateY(40px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/')}
              sx={{
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <RocketLaunch />
              Whatzneu Services
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RocketLaunch />}
            onClick={() => navigate('/')}
            sx={{
              background: 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.6)',
              },
            }}
          >
            Back to Home
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 12, md: 14 }, pb: 6 }}>
        {/* Hero Section */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 8,
            animation: 'fadeInUp 0.8s ease-out',
          }}
        >
          <Chip
            icon={<AutoAwesome />}
            label="Comprehensive Business Services"
            sx={{
              mb: 3,
              background: 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              fontWeight: 700,
              fontSize: '1rem',
              height: 40,
              px: 1,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              color: 'white',
              mb: 3,
              fontSize: { xs: '2.5rem', md: '4rem' },
              lineHeight: 1.2,
              background: 'linear-gradient(135deg, #fff 0%, #f0f0f0 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Beyond Financing
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: alpha(theme.palette.common.white, 0.95),
              maxWidth: '800px',
              mx: 'auto',
              fontWeight: 400,
              mb: 4,
              fontSize: { xs: '1.1rem', md: '1.5rem' },
            }}
          >
            We're not just your lender—we're your growth partner. Our comprehensive services help
            SMEs optimize operations, leverage technology, and achieve sustainable growth.
          </Typography>
        </Box>

        {/* Services Grid */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {services.map((service, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: `fadeInUp 0.8s ease-out ${index * 0.1}s both`,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: service.gradient,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-10px) scale(1.02)',
                    background: 'rgba(255, 255, 255, 0.25)',
                    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
                    border: `1px solid ${service.color}`,
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 3,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(service.color, 0.2)}, ${alpha(service.color, 0.1)})`,
                        border: `2px solid ${alpha(service.color, 0.3)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Box sx={{ color: service.color }}>{service.icon}</Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 800,
                          color: 'white',
                          mb: 2,
                          fontSize: { xs: '1.5rem', md: '1.8rem' },
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: alpha(theme.palette.common.white, 0.95),
                          lineHeight: 1.8,
                          fontSize: '1.05rem',
                          mb: 3,
                        }}
                      >
                        {service.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: `1px solid ${alpha(service.color, 0.2)}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        mb: 2,
                        fontSize: '1rem',
                      }}
                    >
                      Key Features:
                    </Typography>
                    <Grid container spacing={1.5}>
                      {service.features.map((feature, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1.5,
                              borderRadius: 1.5,
                              background: 'rgba(255, 255, 255, 0.05)',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                background: 'rgba(255, 255, 255, 0.1)',
                                transform: 'translateX(5px)',
                              },
                            }}
                          >
                            <VerifiedUser
                              sx={{
                                fontSize: 18,
                                color: service.color,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: alpha(theme.palette.common.white, 0.9),
                                fontSize: '0.95rem',
                              }}
                            >
                              {feature}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            borderRadius: 4,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            p: 6,
            textAlign: 'center',
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: 'white',
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Ready to Transform Your Business?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: alpha(theme.palette.common.white, 0.9),
              mb: 4,
              fontWeight: 400,
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            Let our expert team help you optimize operations, leverage technology,
            and achieve sustainable growth. Get started today!
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'center' }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Phone />}
              onClick={() => navigate('/')}
              sx={{
                py: 1.5,
                px: 5,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.35)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Contact Us
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<RocketLaunch />}
              onClick={() => navigate('/')}
              sx={{
                py: 1.5,
                px: 5,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                  transform: 'translateY(-3px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Apply for Loan
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesPage;
